import { useEffect, useRef } from 'react'
import { useUserPref } from '../hooks/useUserPref'

/**
 * MagicCursorCanvas — опциональный эстетический эффект «Магические движения».
 *
 * Трейл-эмиттер частиц за курсором (как на ggmed.ru, но в 2D-canvas вместо
 * тяжёлого THREE.js/WebGL). На движении мыши вдоль траектории спавнятся
 * светящиеся частицы; каждая разлетается со случайной скоростью с затуханием,
 * тает (life→0) и уменьшается → получается мерцающий шлейф «звёздной пыли».
 * Когда курсор стоит — новые не спавнятся, шлейф плавно гаснет.
 *
 * Полноэкранный неинтерактивный canvas (pointer-events off), цвет берётся из
 * темы (--primary). Аддитивное смешивание ('lighter') даёт свечение на наложении.
 * Само-гейтится pref `cb-magic-motion`: выключено → null, RAF не крутится.
 */
export function MagicCursorCanvas() {
  const [enabled] = useUserPref<boolean>('cb-magic-motion', false)
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!enabled) return
    // На тач/мобильных устройствах нет «парящего» курсора — pointermove
    // летит только при касании/скролле. Эффект там не имеет смысла и мешал бы,
    // поэтому полностью отключаем (слушатели/RAF не навешиваются).
    const coarse =
      typeof window.matchMedia === 'function' &&
      (window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches)
    if (coarse) return
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    let w = 0
    let h = 0
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    const resize = () => {
      w = window.innerWidth
      h = window.innerHeight
      canvas.width = Math.max(1, Math.floor(w * dpr))
      canvas.height = Math.max(1, Math.floor(h * dpr))
      canvas.style.width = w + 'px'
      canvas.style.height = h + 'px'
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()

    // Спрайт частицы — мягкий радиальный градиент в цвет темы. Рисуем
    // drawImage'ом (дёшево) вместо createRadialGradient каждый кадр.
    // Пересобираем при смене темы.
    // ggmed: текстура = СПЛОШНОЙ диск (fillStyle rgba(255,255,255,1), arc r=7
    // в canvas 16px), а НЕ мягкий градиент. Повторяем: плотная заливка до ~88%
    // радиуса, затем 1-2px фезер (имитация antialias:true у THREE-плоскости).
    const SPRITE = 64
    const sprite = document.createElement('canvas')
    sprite.width = SPRITE
    sprite.height = SPRITE
    const sctx = sprite.getContext('2d')!
    let baseColor = 'oklch(0.65 0.18 280)'
    const colorAt = (a: number) => {
      if (/^(oklch|hsl|rgb|lab|lch)/.test(baseColor)) {
        return baseColor.replace(/\)\s*$/, ` / ${a})`)
      }
      return baseColor
    }
    const buildSprite = () => {
      const v = getComputedStyle(document.documentElement)
        .getPropertyValue('--primary').trim()
      if (v) baseColor = v
      sctx.clearRect(0, 0, SPRITE, SPRITE)
      const c = SPRITE / 2
      const g = sctx.createRadialGradient(c, c, 0, c, c, c)
      // Плотная сердцевина (как сплошной диск r=7/8 у ggmed) + узкий мягкий край.
      g.addColorStop(0.0, colorAt(1.0))
      g.addColorStop(0.82, colorAt(1.0))
      g.addColorStop(0.90, colorAt(0.0))
      g.addColorStop(1.0, colorAt(0.0))
      sctx.fillStyle = g
      sctx.beginPath()
      sctx.arc(c, c, c, 0, Math.PI * 2)
      sctx.fill()
    }
    buildSprite()
    const colorTimer = window.setInterval(buildSprite, 2000)

    type P = {
      x: number; y: number; vx: number; vy: number
      life: number; decay: number; scale: number
    }
    const MAX = 2000
    const ps: P[] = []

    // Не «пачкаем» важные элементы интерфейса: над текстом, контролами,
    // ссылками, иконками частицы не спавним. Эффект остаётся только на
    // пустом фоне / в отступах. Escape-hatch: data-no-magic / .no-magic.
    const SKIP_SELECTOR = [
      'a', 'button', 'input', 'textarea', 'select', 'label', 'summary',
      'option', 'svg', 'img', 'canvas', 'video', 'code', 'pre', 'kbd',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'b', 'strong',
      'em', 'i', 'small', 'li', 'td', 'th', 'dt', 'dd',
      '[role="button"]', '[role="link"]', '[role="tab"]', '[role="menuitem"]',
      '[role="textbox"]', '[role="checkbox"]', '[role="switch"]', '[role="option"]',
      '[contenteditable=""]', '[contenteditable="true"]',
      '[data-no-magic]', '.no-magic',
    ].join(',')
    // Прямой текст в элементе (текстовый узел с непробельным содержимым).
    // Ловит «голые» <div>-строки логов / ячейки результатов, которые не
    // попадают под теговый селектор, но фактически содержат текст.
    const hasDirectText = (node: Element): boolean => {
      const kids = node.childNodes
      for (let i = 0; i < kids.length; i++) {
        const k = kids[i]
        if (k.nodeType === 3 && (k.textContent || '').trim().length > 0) return true
      }
      return false
    }
    const overUI = (el: EventTarget | null): boolean => {
      const node = el as Element | null
      if (!node || typeof node.closest !== 'function') return false
      if (node.closest(SKIP_SELECTOR)) return true
      // Элемент под курсором содержит собственный текст → не «пачкаем».
      if (hasDirectText(node)) return true
      return false
    }

    let lastX = 0
    let lastY = 0
    let hasPrev = false
    const spawn = (x: number, y: number) => {
      if (!hasPrev) { lastX = x; lastY = y; hasPrev = true; return }
      const dx = x - lastX
      const dy = y - lastY
      const dist = Math.hypot(dx, dy)
      lastX = x; lastY = y
      if (dist < 0.5) return
      // Плотность спавна вдоль пути ∝ скорости (как у ggmed: dist/1, до 15).
      const n = Math.max(1, Math.min(15, Math.floor(dist)))
      for (let i = 0; i < n; i++) {
        if (ps.length >= MAX) break
        const t = i / n
        // Спавн строго вдоль пути (как ggmed: prevMouse + d*t, без джиттера).
        ps.push({
          x: lastX - dx * (1 - t),
          y: lastY - dy * (1 - t),
          vx: (Math.random() - 0.5) * 5,      // ±2.5 — их значение
          vy: (Math.random() - 0.5) * 5,
          life: 1,
          decay: 0.005 + Math.random() * 0.005, // их 0.005-0.01 → жизнь ~2-3с
          scale: 0.6 + Math.random() * 0.7,      // их initialScale 0.6-1.3
        })
      }
    }
    const onMove = (e: PointerEvent) => {
      if (overUI(e.target)) {
        // Над важным элементом — не спавним, но фиксируем позицию,
        // чтобы при возврате на фон не нарисовать длинную «склейку».
        lastX = e.clientX; lastY = e.clientY; hasPrev = true
        return
      }
      spawn(e.clientX, e.clientY)
    }

    const loop = () => {
      ctx.clearRect(0, 0, w, h)
      ctx.globalCompositeOperation = 'lighter'
      for (let i = ps.length - 1; i >= 0; i--) {
        const p = ps[i]
        p.x += p.vx
        p.y += p.vy
        p.vx *= 0.98
        p.vy *= 0.98
        p.life -= p.decay
        if (p.life <= 0) { ps.splice(i, 1); continue }
        // ggmed: плоскость 7px × initialScale × life, диск ~88% → видимый
        // диаметр ≈ 6.1·scale·life (≈3.7–7.9px на рождении, тает до 0).
        const sz = 7 * p.scale * p.life
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life))
        ctx.drawImage(sprite, p.x - sz / 2, p.y - sz / 2, sz, sz)
      }
      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
      raf = requestAnimationFrame(loop)
    }

    window.addEventListener('resize', resize)
    window.addEventListener('pointermove', onMove, { passive: true })
    raf = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(raf)
      window.clearInterval(colorTimer)
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', onMove)
    }
  }, [enabled])

  if (!enabled) return null

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9998,
      }}
    />
  )
}
